import { useState } from 'react';
import { useTranslate } from 'react-admin';
import { Alert, Stack, Typography } from '@mui/material';

import {
  approveWorkOrderResolution,
  cancelWorkOrderResolution,
  getWorkOrder,
  proposeWorkOrderResolution,
  type QueueList,
  type WorkOrderItem,
  type WorkOrderResolution,
} from 'src/pages/admin/services/vaultOperationsService';

import { ConfirmDialog, type ConfirmSpec } from './ConfirmDialog';
import { OPERATIONS_NS as NS } from './constants';
import {
  adminName,
  formatInstant,
  middle,
  money2,
  money6,
  operationsErrorMessages,
  requiredText,
} from './helpers';
import {
  ActionButton,
  AddressLink,
  EmptyState,
  ItemCard,
  ListNotices,
  nodes,
  ProgressStrip,
  TabIntro,
  TimeText,
} from './parts';
import type { Queue } from './useQueue';

interface Props {
  queue: Queue<QueueList<WorkOrderItem>>;
  vaultName: (vaultId: string | null | undefined) => string;
  onSettled: () => Promise<unknown>;
}

/** "Payouts needing a decision": UNRESOLVED treasury work orders. Nobody is on a clock; people decide. */
export const PayoutsTab = ({ queue, vaultName, onSettled }: Props) => {
  const translate = useTranslate();
  const t = (key: string, options?: object) => translate(`${NS}.payouts.${key}`, options);
  const c = (key: string, options?: object) => translate(`${NS}.confirm.${key}`, options);
  const [spec, setSpec] = useState<ConfirmSpec | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const withdraw = (item: WorkOrderItem, resolution: WorkOrderResolution) =>
    setSpec({
      title: t('withdrawTitle'),
      summary: <ProgressStrip proposal={resolution} youProposed={resolution.youProposed} />,
      moneyMoves: false,
      money: c('withdrawNoMoney'),
      approver: c('noApproval'),
      ifNobody: t('withdrawIfNobody'),
      confirmLabel: t('withdraw'),
      fields: [],
      submit: () => cancelWorkOrderResolution(item.orderId, resolution.id),
      doneMessage: t('done.cancel'),
    });

  /**
   * What a FAILED resolution lets be paid again: what LEAVES (`sentAmount`, only on the detail read) with its source, and the
   * owed amount beside it when they differ. Without a readable sentAmount the owed amount is shown as owed, never as sent.
   */
  const failedMoney = (
    key: 'approveFailed' | 'proposeFailed',
    item: WorkOrderItem,
    evidence: { sentAmount: string | null; source?: string | null } | null
  ) => {
    const owed = money6(item.amount);
    const destination = <AddressLink address={item.destination} />;
    if (!evidence?.sentAmount) {
      return nodes((v) => t(`money.${key}Owed`, { owed, ...v }), { destination });
    }
    const sent = money6(evidence.sentAmount);
    return nodes((v) => t(`money.${key}`, { sent, owed, ...v }), {
      destination,
      from: evidence.source
        ? nodes((w) => t('money.fromSource', w), {
            source: <AddressLink address={evidence.source} />,
          })
        : '',
    });
  };

  const approve = async (item: WorkOrderItem, resolution: WorkOrderResolution) => {
    const failed = resolution.outcome === 'FAILED';
    const amount = money6(item.amount);
    let evidence: { sentAmount: string | null; source?: string | null } | null = null;
    if (failed) {
      setBusy(item.orderId);
      setProblem(null);
      try {
        const detail = await getWorkOrder(item.orderId);
        // Withdrawn or applied since the list was read: the approval would be a 404.
        if (detail.resolution?.id !== resolution.id) {
          setProblem(t('stale'));
          await onSettled();
          return;
        }
        evidence = detail.evidence;
      } catch {
        // the dialog then shows the owed amount as owed and says the sent amount could not be read
      } finally {
        setBusy(null);
      }
    }
    setSpec({
      title: failed ? t('approveFailedTitle') : t('approveConfirmedTitle'),
      summary: (
        <Stack spacing={0.5}>
          <ProgressStrip proposal={resolution} youProposed={false} />
          <Typography variant="body2">
            {c('proposerNote', {
              name: adminName(resolution.proposedByAdmin, resolution.proposedBy),
            })}{' '}
            “{resolution.note}”
          </Typography>
          <Typography variant="body2">
            {c('evidence')}: {resolution.evidenceRef}
          </Typography>
        </Stack>
      ),
      moneyMoves: item.nextStep.moneyMoves,
      money: failed
        ? failedMoney('approveFailed', item, evidence)
        : nodes(
            (v) => t('money.approveConfirmed', { amount, txHash: resolution.txHash ?? '—', ...v }),
            { destination: <AddressLink address={item.destination} /> }
          ),
      approver: c('approverFinal'),
      ifNobody: c('ifNobodyApprove'),
      stepUpReason: failed ? t('stepUp.failed') : t('stepUp.confirmed'),
      confirmLabel: failed ? t('approveFailed') : t('approveConfirmed'),
      fields: [],
      submit: () => approveWorkOrderResolution(item.orderId, resolution.id),
      doneMessage: t('done.approve'),
    });
  };

  const propose = async (item: WorkOrderItem, outcome: 'CONFIRMED' | 'FAILED') => {
    setBusy(item.orderId);
    setProblem(null);
    try {
      const detail = await getWorkOrder(item.orderId);
      // Someone proposed, or the order moved on, since the list was read: do not open a dialog the BFF would refuse.
      if (detail.resolution || detail.nextStep.kind !== 'PROPOSE_RESOLUTION') {
        setProblem(t('stale'));
        await onSettled();
        return;
      }
      const verdict = outcome === 'CONFIRMED' ? detail.evidence.confirm : detail.evidence.fail;
      const candidates = detail.evidence.venue.candidates;
      const confirmed = outcome === 'CONFIRMED';
      const amount = money6(item.amount);
      setSpec({
        title: confirmed ? t('proposeConfirmedTitle') : t('proposeFailedTitle'),
        moneyMoves: false,
        money: confirmed
          ? t('money.proposeConfirmed')
          : failedMoney('proposeFailed', item, detail.evidence),
        approver: c('approverNext'),
        ifNobody: c('ifNobodyPropose'),
        confirmLabel: confirmed ? t('proposeConfirmed') : t('proposeFailed'),
        blocked:
          verdict && !verdict.accepted
            ? t('evidenceRefuses', { reason: verdict.reason ?? verdict.code ?? '' })
            : confirmed && candidates.length === 0
              ? t('noCandidates')
              : undefined,
        fields: [
          ...(confirmed
            ? [
                {
                  name: 'txHash',
                  label: t('pickTransfer'),
                  options: candidates.map((x) => ({
                    value: x.txHash,
                    label: `${middle(x.txHash)} · ${money2(x.amount)} USDC · ${formatInstant(x.observedAt)}`,
                  })),
                  validate: (v: string) => (v ? undefined : `${NS}.errors.required`),
                },
              ]
            : []),
          {
            name: 'evidenceRef',
            label: c('evidenceRef'),
            hint: c('evidenceHint'),
            validate: requiredText,
          },
          { name: 'note', label: c('note'), multiline: true, validate: requiredText },
        ],
        submit: (v, requestId) =>
          proposeWorkOrderResolution(item.orderId, {
            outcome,
            ...(confirmed ? { txHash: v.txHash } : {}),
            evidenceRef: v.evidenceRef,
            note: v.note,
            requestId,
          }),
        doneMessage: t('done.propose'),
      });
    } catch (error) {
      setProblem(operationsErrorMessages(error, translate)[0]);
    } finally {
      setBusy(null);
    }
  };

  const items = queue.data?.items ?? [];
  return (
    <Stack spacing={2}>
      <TabIntro tab="payouts" />
      {problem && <Alert severity="error">{problem}</Alert>}
      {queue.data && (
        <ListNotices
          consistent={queue.data.consistent}
          truncated={queue.data.truncated}
          shown={items.length}
          onRefresh={queue.refetch}
        />
      )}
      {queue.data && items.length === 0 && (
        <EmptyState tab="payouts" checkedAt={queue.data.checkedAt} />
      )}
      {items.map((item) => {
        const resolution = item.resolution;
        const kind = item.nextStep.kind;
        const vault = vaultName(item.vaultId);
        return (
          <ItemCard
            key={item.orderId}
            testId={`payout-${item.orderId}`}
            title={t('title', { amount: money2(item.amount) })}
            status={item.uxStatus}
            whatHappened={translate(
              `${NS}.whatHappened.${
                resolution?.status === 'AWAITING_APPROVAL'
                  ? resolution.outcome === 'FAILED'
                    ? 'payoutsProposedFailed'
                    : 'payoutsProposedConfirmed'
                  : 'payouts'
              }`
            )}
            affected={
              <Stack
                direction="row"
                spacing={0.5}
                useFlexGap
                sx={{ alignItems: 'center', flexWrap: 'wrap' }}
              >
                <span>{t('affected', { vault, amount: money2(item.amount) })}</span>
                <AddressLink address={item.destination} explorerText={t('checkExplorer')} />
              </Stack>
            }
            extra={
              resolution && (
                <Stack spacing={0.5}>
                  <ProgressStrip proposal={resolution} youProposed={resolution.youProposed} />
                  <Typography variant="body2">
                    {t(resolution.outcome === 'FAILED' ? 'proposedFailed' : 'proposedConfirmed')} “
                    {resolution.note}”
                  </Typography>
                </Stack>
              )
            }
            deadline={
              <>
                {t('noDeadline')} {t('waitingSince')} <TimeText iso={item.waitingSince} />
              </>
            }
            actions={
              <>
                {kind === 'PROPOSE_RESOLUTION' && (
                  <>
                    <ActionButton
                      needs="propose"
                      variant="contained"
                      disabled={busy === item.orderId}
                      onClick={() => propose(item, 'CONFIRMED')}
                    >
                      {t('proposeConfirmed')}
                    </ActionButton>
                    <ActionButton
                      needs="propose"
                      variant="outlined"
                      disabled={busy === item.orderId}
                      onClick={() => propose(item, 'FAILED')}
                    >
                      {t('proposeFailed')}
                    </ActionButton>
                  </>
                )}
                {kind === 'WAIT_FOR_SECOND_APPROVER' && resolution && (
                  <>
                    <ActionButton
                      needs="propose"
                      variant="outlined"
                      onClick={() => withdraw(item, resolution)}
                    >
                      {t('withdraw')}
                    </ActionButton>
                  </>
                )}
                {kind === 'APPROVE_RESOLUTION' && resolution && (
                  <>
                    <ActionButton
                      needs="approve"
                      variant="contained"
                      disabled={busy === item.orderId}
                      onClick={() => approve(item, resolution)}
                    >
                      {resolution.outcome === 'FAILED' ? t('approveFailed') : t('approveConfirmed')}
                    </ActionButton>
                    <ActionButton
                      needs="propose"
                      variant="outlined"
                      onClick={() => withdraw(item, resolution)}
                    >
                      {t('reject')}
                    </ActionButton>
                  </>
                )}
              </>
            }
            technical={{
              orderId: item.orderId,
              status: item.status,
              redemptionRecordId: item.redemptionRecordId,
              destination: item.destination,
              ...item.technical,
              proposalId: resolution?.id,
            }}
          />
        );
      })}
      {spec && <ConfirmDialog spec={spec} onClose={() => setSpec(null)} onSettled={onSettled} />}
    </Stack>
  );
};
